/* eslint-disable camelcase */
import Logger from './logger';
import { CascadingConfig } from './cascading_config';

/**
 * The required params for each header bidding partner
 * @type {Object}
 */
const REQUIRED_PREBID_PARAMS = {
  smartadserver: ['siteId', 'pageId', 'formatId'],
  onemobile: ['pos', 'dcn'],
  appnexus: ['placementId'],
  districtm: ['placementId'],
  districtmDMX: ['dmxid', 'memberid'],
  concert: ['partnerId'],
  consumable: ['networkId', 'siteId', 'unitId', 'unitName'],
  ix: ['siteId', 'size'],
  nobid: ['siteId', 'placementId'],
  openx: ['unit', 'delDomain'],
  pubmatic: ['publisherId'],
  rhythmone: ['placementId'],
  rubicon: ['accountId', 'siteId', 'zoneId', 'keywords', 'inventory', 'position'],
  rubiconLite: ['accountId', 'siteId', 'zoneId', 'sizes', 'position'],
  sharethrough: ['pkey'],
  sonobi: ['placement_id', 'ad_unit'],
  sovrn: ['tagid'],
  teads: ['pageId', 'placementId'],
  triplelift: ['inventoryCode'],
  trustx: ['uid'],
};

const OPTIONAL_PREBID_PARAMS = {
  concert: ['site', 'placementId', 'slot', 'sizes'],
};

/**
 * A Bid is a representation of a Slot's header bids. There is a 1:1 relationship
 * between a Bid and a Slot. The term "bid" is a bit misleading, because a slot
 * can (and will) have many bidding partner configurations, and each configuration
 * can return multiple bids. However, this Bid class is responsible for:
 * 1. Setting up the bid process for a given slot
 * 2. Sending the targeting, returned from bids, back to the Slot
 *
 * A new Bid will be created each time a slot is displayed, including refreshes.
 *
 * A Bid sends itself to the BidManager class when ready.
 */
export default class Bid {
  constructor(slot) {
    this.slot = slot;
    this.app = slot.app;

    // Bail early if Prebid is disabled globally
    if (!this.app.settings.prebid.biddingEnabled) {
      this.slot.markPrebidIneligible();
      return;
    }

    if (this.app.settings.prebid.prebidEnabled) {
      this.config = configForSlot.call(this);
    } else {
      this.config = true;
    }

    // If there is no prebid config for this slot,
    // return before attempting to fetch bids and also mark the slot as not
    // prebidEligible (although it may already be).
    if (!this.isEnabled()) {
      this.slot.markPrebidIneligible();
      return;
    }

    // Make sure we have some sizes associated with
    // the slot in order to start bidding
    if (!this.slot.sizes || this.slot.sizes.length === 0) {
      this.slot.markPrebidIneligible();
      return;
    }

    // Mark the slot as actively prebidding. This allows us to defer loading
    // of an ad slot until prebidding is complete (or timed out)
    this.slot.bidding();

    this.app.bidManager.addBid(this);
  }

  get prebidSettings() {
    return this.app.settings.prebid;
  }

  get pageContentInfo() {
    let { cts_keyword, entry_group, hub_page, keywords } = this.app.variables;

    if (typeof keywords === 'string') {
      keywords = keywords.split(' ');
    }

    const pageContentConfig = {
      rubicon: {
        keywords: keywords ? keywords : [''],
        inventory: {
          ctsKeywords: cts_keyword || [''],
          entryGroup: entry_group || [''],
          hubPage: hub_page || [''],
        },
      },
    };

    return pageContentConfig;
  }

  /**
   * Is prebid enabled for this slot?
   * @return {boolean}
   */
  isEnabled() {
    return this.slot.isPrebidEligible() && this.slot.isDisplayEligible() && !!this.config;
  }

  /**
   * Add targeting to the DFP slot
   */
  addTargeting() {
    googletag.cmd.push(() => {
      var prebidTargetingForSlot = pbjs.getAdserverTargetingForAdUnitCode(this.slot.name);

      // If this returns null, which is always possible.
      if (!prebidTargetingForSlot) {
        this.slot.bidded();
        return;
      }

      Logger.log(`Setting Prebid targeting for ${this.slot.name}.`, {
        prebidTargeting: prebidTargetingForSlot,
        slotName: this.slot.name,
      });

      // Go into all the information sent back from Prebid and add it
      Object.keys(prebidTargetingForSlot).forEach(key => {
        this.slot.setTargeting(key, prebidTargetingForSlot[key]);
      });
    });
  }
}

/**
 * Get a list of bids and bidders for a slot
 * @param  {Bid} bid
 * @return {array}
 */
function configForSlot() {
  var bids = [];
  const prebidName = this.slot.prebidName || this.slot.configName;
  const prebidInfoforSlot = this.prebidSettings[prebidName] || {};

  if (prebidInfoforSlot.disabled) {
    return false;
  }

  // Go through all the bidders we allow and create an object for each one.
  Object.keys(REQUIRED_PREBID_PARAMS).forEach(bidder => {
    const bidderParams = prebidInfoforSlot[bidder] || {};
    const defaultBidderParams = (this.prebidSettings.defaultConfig || {})[bidder] || {};
    const pageContentParams = this.pageContentInfo[bidder] || {};
    const paramSources = new CascadingConfig('bid-params');
    paramSources.add(defaultBidderParams);
    paramSources.add(pageContentParams);
    paramSources.add(bidderParams);

    // Allow us to disable a single slot or partner by configuration.
    if (paramSources.get('disabled')) {
      return;
    }

    const requiredParams = REQUIRED_PREBID_PARAMS[bidder];
    const optionalParams = OPTIONAL_PREBID_PARAMS[bidder] || [];
    const bidsForBidder = bidderRequiresIndividualSizes(requiredParams)
      ? this.slot.sizes.map(size => buildBidParams(requiredParams, optionalParams, paramSources, size))
      : [buildBidParams(requiredParams, optionalParams, paramSources)];

    bidsForBidder.forEach(params => {
      if (params === false) return;
      bids.push({ bidder, params });
    });
  });

  // If we didn't add any bids in there, then we will return false.
  if (bids.length === 0) {
    bids = false;
  }

  return bids;
}

/**
 * Build up a `params` object for a Prebid bid. Accepts a series of objects which
 * cascade from left to right.
 *
 * @param {object}            requiredParams
 * @param {CascadingConfig}   Config of sources to resolve
 * @param {object}            Size params [1030, 590] in the case of a bidder needing them
 */
function buildBidParams(requiredParams, optionalParams, sources, size) {
  let requiredBidParams = requiredParams.reduce((memo, param) => {
    if (param === 'size' && size) {
      memo.size = size;
      return memo;
    }

    memo[param] = sources.get(param) || false;
    return memo;
  }, {});

  // If any required keys are missing, bail
  if (!Object.values(requiredBidParams).every(Boolean)) {
    return false;
  }

  let optionalBidParams = optionalParams.reduce((memo, param) => {
    if (sources.get(param)) memo[param] = sources.get(param);
    return memo;
  }, {});

  return { ...optionalBidParams, ...requiredBidParams };
}

/**
 * Determine whether a bidder requires individual sizes enumerated in bids.
 *
 * @param {object} requiredParams
 */
function bidderRequiresIndividualSizes(requiredParams) {
  return requiredParams.indexOf('size') > -1;
}
